<script lang="ts">
import tablePageInit from "../packages/datakode/nuxt-datakode-utils/utils/tablePageInit.vue"
import { get, set, useOnlineStore } from "../store"

export default {
    name: "OfflineTablePageInit",
    extends: tablePageInit,

    computed: {
        hasPermissionOnTable(): boolean {
            const permission = `${this.permissionName}_index`
            return this.$permissionCheck(permission)
        },
    },
    mounted() {
        if (!this.$permissionCheck(`${this.permissionName}_page`)) {
            this.$showToaster({ text: "Vous n'avez pas les droits suffisants pour accéder à cet espace.", color: "error", icon: "mdi-information" })
            this.$router.replace("/")
        }
        // Preload metadata from API if online and save to local db, otherwise read from local db
        this.preLoadDataMeta()
        this.checkFiltersParams()
    },
    methods: {
        checkFiltersParams() {
            this.loadingQuery = this.$route?.query
        },
        async preLoadDataMeta() {
            if (this.hasPermissionOnTable) {
                if (useOnlineStore().online) {
                    this.$api.getListData({
                        url: this.requestName,
                        translation: this.translationName,
                        pagination: { number: 1, size: 1 },
                    }).then((res) => {
                        this.meta = res.meta
                        this.filters = res.meta.allowed_filters
                        // IF NO DEFAULT FILTER -> RELOAD DATA / ELSE FILTERS WATCH WILL RELOAD DATA
                        if (!this.filters.find(filter => filter.initial_value)) {
                            this.loadData()
                        }
                    }).catch((e) => {
                        this.loading = false
                        console.error(e)
                        throw createError({
                            statusCode: 404,
                            fatal: true,
                            statusMessage: "Page Not Found",
                        })
                    })
                }
                else if (window && window.indexedDB) {
                    const res = await get(`${this.requestName}-meta`)
                    this.meta = res
                    this.filters = res.allowed_filters
                    this.loadData()
                }
            }
        },
        async loadData() {
            if (this.hasPermissionOnTable) { // KEEP PERMISSION CHECK HERE FOR RELOADS
                this.loading = true
                const filterQuery = this.$refs.filters?.getQuery() || {}
                const paginationQuery = this.$refs.pagination?.getQuery() || null
                const sort = this.$refs.table?.sort || null
                if (this.customQuery?.customQueryKey && this.customQuery?.queryId) {
                    filterQuery[this.customQuery.customQueryKey] = this.customQuery.queryId
                }
                if (useOnlineStore().online) {
                    this.loading = true
                    this.$api.getListData({
                        url: this.requestName,
                        query: filterQuery,
                        translation: this.translationName,
                        pagination: paginationQuery,
                        sort,
                    }).then((res) => {
                        this.data = res.data
                        this.meta = res.meta
                        this.filters = res.meta.allowed_filters
                        this.sortable = res.meta.allowed_sorts
                        this.exportable = res.meta.available_exports
                        this.lastPage = res.meta.last_page
                        this.total = res.meta.total
                        // this.sort = res.meta.request.sort

                        // RESET SELECTED DATA FROM TABLE IF EXIST
                        if (this.$refs.table) {
                            this.$refs.table.resetSelectedData()
                        }
                        this.loading = false
                    }).catch(() => {
                        this.loading = false
                        throw createError({
                            statusCode: 404,
                            fatal: true,
                            statusMessage: "Page Not Found",
                        })
                    })
                }
                else if (window && window.indexedDB) {
                    this.loading = true

                    // TimeOut need to let vuetify create loading animation
                    setTimeout(async () => {
                        try {
                            // OFFLINE, load from local db
                            this.data = await get(`${this.requestName}-data`)
                            const meta = await get(`${this.requestName}-meta`)
                            this.meta = meta
                            this.filters = meta.allowed_filters
                            this.sortable = meta.allowed_sorts
                            this.exportable = meta.available_exports

                            if (sort && sort.order) {
                                this.data = this.data.sort((a, b) => {
                                    const k = sort.key
                                    let v1 = a[k]
                                    let v2 = b[k]
                                    if (sort.key.includes(".")) {
                                        const n = sort.key.split(".")
                                        v1 = a[n[0]] ? a[n[0]][n[1]] : null
                                        v2 = b[n[0]] ? b[n[0]][n[1]] : null
                                    }
                                    if (sort.order.toLowerCase() === "asc") {
                                        return v1 < v2 ? 1 : -1
                                    }
                                    return v1 < v2 ? -1 : 1
                                })
                            }

                            if (filterQuery) {
                                this.data = this.data.filter((item) => {
                                    for (const f in filterQuery) {
                                        const filterVal = filterQuery[f]

                                        if (filterVal === null || filterVal === undefined || filterVal === "")
                                            continue
                                        if (Array.isArray(filterVal) && filterVal.length === 0)
                                            continue

                                        if (f === "epci_commune_id" || f === "limit_to_accessible_for") {
                                            let allowedIds = Array.isArray(filterVal) ? [...filterVal] : [filterVal]
                                            allowedIds = allowedIds.map(id => String(id))

                                            if (this.currentEpciCommune?.id && allowedIds.includes(String(this.currentEpciCommune.id))) {
                                                if (this.currentEpciCommune.accessible_communes) {
                                                    this.currentEpciCommune.accessible_communes.forEach((ac) => {
                                                        allowedIds.push(String(ac.id))
                                                    })
                                                }
                                            }

                                            const itemCommuneId = item.epci_commune_id
                                            if (!allowedIds.includes(String(itemCommuneId)))
                                                return false
                                            continue
                                        }

                                        let itemValue = item[f]
                                        if (itemValue === undefined && f.endsWith("_id")) {
                                            const relationName = f.replace("_id", "")
                                            if (item[relationName]) {
                                                itemValue = item[relationName].id || item[relationName].k
                                            }
                                        }

                                        if (itemValue === null || itemValue === undefined)
                                            return false

                                        if (Array.isArray(filterVal)) {
                                            if (!filterVal.map(String).includes(String(itemValue)))
                                                return false
                                        }
                                        else {
                                            if (String(itemValue) !== String(filterVal))
                                                return false
                                        }
                                    }

                                    return true
                                })
                            }
                            this.total = this.data.length
                            if (paginationQuery && paginationQuery.size) {
                                this.lastPage = Math.ceil(this.total / paginationQuery.size) || 1
                                const start = (paginationQuery.number - 1) * paginationQuery.size
                                const end = paginationQuery.number * paginationQuery.size

                                this.data = this.data.slice(start, end)
                            }
                            else {
                                this.lastPage = 1
                            }
                        }
                        catch (error) {
                            console.error("Erreur offline :", error)
                        }
                        finally {
                            this.loading = false
                        }
                    }, 50)
                }
            }
            else {
                console.error(`Permission needed: ${permission}`)
            }
        },
        getCreateRequest() { // FOR SPECIAL CREATE
            const keys = Object.keys(this.createData)
            let query = ""
            keys.forEach((key, i) => {
                query += i === 0 ? "?" : "&"
                query += `${key}=${this.createData[key]}`
            })
            return `${this.defaultUrl}/nouveau/formulaire${query}`
        },
        async deleteData(item) {
            if (item.k) {
                if (useOnlineStore().online) {
                    await this.$api.setRequest({
                        url: `${this.requestName}/${item.k}`,
                        method: "DELETE",
                        translation: this.translationName,
                        body: item,
                    })
                    this.loadData()
                }
                else if (window && window.indexedDB) {
                    // Save item to delete
                    let existingToDelete = []
                    existingToDelete = await get(`${this.requestName}-to-delete`) || []
                    // eslint-disable-next-line eqeqeq
                    if (existingToDelete.filter(e => e.k == item.k).length === 0 && item.id) {
                        existingToDelete.push(item)
                    }

                    await set(`${this.requestName}-to-delete`, existingToDelete)

                    // remove item from list
                    this.data = this.data.filter((el) => {
                        // eslint-disable-next-line eqeqeq
                        return el.k != item.k
                    })
                    await set(`${this.requestName}-data`, this.data)

                    // remove item from post and put lists
                    let existingToPost = await get(`${this.requestName}-to-post`) || []

                    // eslint-disable-next-line eqeqeq
                    existingToPost = existingToPost.filter(e => e.k != item.k)
                    await set(`${this.requestName}-to-post`, existingToPost)

                    let existingToPut = await get(`${this.requestName}-to-put`) || []

                    // eslint-disable-next-line eqeqeq
                    existingToPut = existingToPut.filter(e => e.k != item.k)
                    await set(`${this.requestName}-to-put`, existingToPut)
                }
            }
        },
    },
}
</script>
